import React, { useState, useRef, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';
import '../styles/CookieImageAnalyzer.css';

// Initialize TensorFlow.js
const initializeTensorFlow = async () => {
  await tf.ready();
  // Use WebGL backend for better performance
  await tf.setBackend('webgl');
  console.log('TensorFlow.js initialized with backend:', tf.getBackend());
};

const CookieImageAnalyzer = ({ onImageProcessed, maxWidth, maxLength, unit, onClose }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [tfReady, setTfReady] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const imageRef = useRef(null);
  
  // Initialize TensorFlow.js when component mounts
  useEffect(() => {
    const init = async () => {
      try {
        await initializeTensorFlow();
        setTfReady(true);
      } catch (err) {
        console.error('Failed to initialize TensorFlow.js:', err);
        setError('Failed to initialize image processing library. Please try again later.');
      }
    };
    
    init();
  }, []);
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };
  
  const processFile = (file) => {
    setProcessing(true);
    setError(null);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageUrl(event.target.result);
    };
    reader.onerror = () => {
      setProcessing(false);
      setError('Failed to read the image file. Please try again.');
    };
    reader.readAsDataURL(file);
  };
  
  // Process the image once it's loaded
  useEffect(() => {
    if (imageUrl && imageRef.current && imageRef.current.complete && tfReady) {
      analyzeCookieImage();
    }
  }, [imageUrl, tfReady]);
  
  // Handle image load event
  const handleImageLoad = () => {
    if (imageUrl && tfReady) {
      analyzeCookieImage();
    }
  };
  
  const captureFromCamera = () => {
    cameraInputRef.current.click();
  };
  
  const uploadFromDevice = () => {
    fileInputRef.current.click();
  };
  
  const analyzeCookieImage = async () => {
    if (!imageRef.current || !tfReady) return;
    
    try {
      setProcessing(true);
      
      // Create a tensor from the image
      const img = imageRef.current;
      const tensor = tf.browser.fromPixels(img);
      
      // Convert to grayscale
      const grayscale = tensor.mean(2);
      
      // Apply Otsu's thresholding to separate cookie from background
      // For simplicity, we'll use a basic threshold
      const threshold = 120;
      const binaryMask = grayscale.less(tf.scalar(threshold));
      
      // Get mask as array to calculate area
      const maskArray = await binaryMask.array();
      
      // Count cookie pixels (white pixels in the binary mask)
      let cookiePixels = 0;
      const totalPixels = maskArray.length * maskArray[0].length;
      
      for (let i = 0; i < maskArray.length; i++) {
        for (let j = 0; j < maskArray[i].length; j++) {
          if (maskArray[i][j]) {
            cookiePixels++;
          }
        }
      }
      
      // Calculate the proportion of the image occupied by the cookie
      const proportion = cookiePixels / totalPixels;
      
      // Calculate the actual area based on max dimensions
      // Assuming the cookie fits within the max dimensions
      const maxArea = maxWidth * maxLength;
      const estimatedArea = maxArea * proportion;
      
      // Calculate estimated dimensions (assuming roughly proportional scaling)
      const aspectRatio = img.width / img.height;
      let estimatedWidth, estimatedLength;
      
      if (aspectRatio >= 1) {
        // Wider than tall
        estimatedWidth = maxWidth;
        estimatedLength = maxWidth / aspectRatio;
      } else {
        // Taller than wide
        estimatedLength = maxLength;
        estimatedWidth = maxLength * aspectRatio;
      }
      
      // Adjust for the area proportion
      const scaleFactor = Math.sqrt(proportion);
      estimatedWidth *= scaleFactor;
      estimatedLength *= scaleFactor;
      
      // Round to 2 decimal places
      const roundedArea = Math.round(estimatedArea * 100) / 100;
      const roundedWidth = Math.round(estimatedWidth * 100) / 100;
      const roundedLength = Math.round(estimatedLength * 100) / 100;
      
      // Set the result
      const analysisResult = {
        area: roundedArea,
        width: roundedWidth,
        length: roundedLength,
        pixelCount: cookiePixels,
        totalPixels: totalPixels,
        proportion: proportion
      };
      
      setResult(analysisResult);
      
      // Pass the result to the parent component
      onImageProcessed(analysisResult);
      
      // Clean up tensors
      tf.dispose([tensor, grayscale, binaryMask]);
      
    } catch (err) {
      console.error('Error analyzing image:', err);
      setError('Failed to analyze the image. Please try again with a different image.');
    } finally {
      setProcessing(false);
    }
  };
  
  const applyResults = () => {
    if (result) {
      onImageProcessed(result);
      onClose();
    }
  };
  
  return (
    <div className="cookie-image-analyzer">
      <h3>Cookie Image Analysis</h3>
      
      {!imageUrl ? (
        <div className="capture-controls">
          <button onClick={captureFromCamera} className="camera-button">
            Take Photo
          </button>
          <button onClick={uploadFromDevice} className="upload-button">
            Upload Image
          </button>
          <input 
            type="file" 
            accept="image/*" 
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        <div className="analysis-container">
          <div className="image-preview">
            <img 
              src={imageUrl} 
              alt="Cookie" 
              ref={imageRef}
              onLoad={handleImageLoad}
            />
          </div>
          
          {processing && (
            <div className="processing-indicator">
              <p>Analyzing image...</p>
              <div className="spinner"></div>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setImageUrl(null)}>Try Again</button>
            </div>
          )}
          
          {result && !processing && !error && (
            <div className="analysis-results">
              <h4>Analysis Results:</h4>
              <p>Estimated Area: {result.area} {unit === 'mm' ? 'cm²' : 'in²'}</p>
              <p>Estimated Width: {result.width} {unit}</p>
              <p>Estimated Length: {result.length} {unit}</p>
              <div className="result-actions">
                <button onClick={applyResults} className="apply-button">
                  Apply Results
                </button>
                <button onClick={() => setImageUrl(null)} className="retry-button">
                  Try Another Image
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="capture-instructions">
        <h4>How to take a good cookie photo:</h4>
        <ul>
          <li>Place cookie on a contrasting background (dark cookie on light background or vice versa)</li>
          <li>Take photo directly from above</li>
          <li>Ensure good lighting (use flash if needed)</li>
          <li>Make sure the cookie fills most of the frame</li>
          <li>Avoid shadows and reflections</li>
        </ul>
      </div>
      
      <button onClick={onClose} className="close-button">
        Cancel
      </button>
    </div>
  );
};

export default CookieImageAnalyzer;
